import CancelIcon from '@eduzz/houston-icons/Cancel';
import UploadIcon from '@eduzz/houston-icons/Upload';
import { IconButton } from '@material-ui/core';
import clsx from 'clsx';
import { useFormikContext } from 'formik';
import * as React from 'react';

import { useStyles } from './styles';

const FILENAME_MAX_SIZE = 25;

type FileInputProps = {
  label: string;
  name: string;
  helperText?: React.ReactNode;
  /**
   * Follows attr accept format from HTML input
   */
  validFormats?: string;
};

function FileInput({ name, label, helperText, validFormats }: FileInputProps) {
  const classes = useStyles();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { setFieldValue, values, errors, getFieldMeta, setFieldTouched } = useFormikContext();

  function handleClick() {
    inputRef?.current.click();
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setFieldTouched(name, true);
    const file = e.target.files[0];
    if (file) {
      setFieldValue(name, file);
    }
  }

  function cleanInput() {
    if (inputRef.current) {
      inputRef.current.value = null;
    }
  }

  function handleRemove(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    setFieldValue(name, null);
    cleanInput();
  }

  const value = values[name] as File | null;
  const hasValue = !!value;
  const errorMessage = errors[name] as string | null;
  const hasError = !!errorMessage && getFieldMeta(name)?.touched;

  const fileName = hasValue && (value.name.length > FILENAME_MAX_SIZE ? `${value.name.slice(0, FILENAME_MAX_SIZE)}...` : value.name);

  return (
    <div className={classes.root}>
      <fieldset className={clsx(classes.fieldset, hasError && '--error')} onClick={handleClick} role='button'>
        <label htmlFor={name}>{label}</label>
        <legend>{label}</legend>
        <input id={name} onChange={handleChange} ref={inputRef} type='file' hidden accept={validFormats} />
        <div className={classes.content}>
          <UploadIcon className={classes.uploadIcon} size={20} />
          {fileName || 'Selecione um arquivo'}
          {hasValue && (
            <IconButton className={classes.removeFileIconBtn} aria-label='Remover arquivo' size='small' onClick={handleRemove}>
              <CancelIcon size={20} />
            </IconButton>
          )}
        </div>
      </fieldset>
      <div className={classes.helperTextContainer}>
        {hasError ? (
          <p role='alert' className={classes.errorMessage}>
            {errorMessage}
          </p>
        ) : (
          <>
            {typeof helperText === 'string' ? (
              <p role='alert' className={classes.helperText}>
                {helperText}
              </p>
            ) : (
              helperText
            )}
          </>
        )}
      </div>
    </div>
  );
}

export { FileInput };
